// Disease model (BIOLOGY-ENGINE.md §3). Every illness has a real cause, an
// incubation period, visible symptoms and a treatment path. Infection rolls are
// gated by immunity, so husbandry — not luck — is what actually decides.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type {
  ActiveCondition,
  ChickenEntity,
  ConditionId,
  ConditionSeverity,
} from '../core/types';
import type { BiologyConfig } from './config';
import type { EnvContext } from './physiology';

export interface DiseaseDefinition {
  id: ConditionId;
  /** Hours between infection and visible symptoms. */
  incubationHours: number;
  /** Hours of correct care before the bird recovers. */
  recoveryHours: number;
  /** Spreads bird-to-bird (mites do; bumblefoot does not). */
  contagious: boolean;
  /** The care action that treats it. */
  remedy: string;
  factCardId: string;
}

export const DISEASES: Record<ConditionId, DiseaseDefinition> = {
  coccidiosis: {
    id: 'coccidiosis',
    incubationHours: 48,
    recoveryHours: 96,
    contagious: true,
    remedy: 'clean_litter_and_medicate',
    factCardId: 'fact_coccidiosis',
  },
  respiratory: {
    id: 'respiratory',
    incubationHours: 36,
    recoveryHours: 120,
    contagious: true,
    remedy: 'ventilate_and_warm',
    factCardId: 'fact_respiratory',
  },
  bumblefoot: {
    id: 'bumblefoot',
    incubationHours: 72,
    recoveryHours: 168,
    contagious: false,
    remedy: 'clean_wrap_foot',
    factCardId: 'fact_bumblefoot',
  },
  scaly_leg_mites: {
    id: 'scaly_leg_mites',
    incubationHours: 96,
    recoveryHours: 168,
    contagious: true,
    remedy: 'oil_legs_and_dust_bath',
    factCardId: 'fact_scaly_leg_mites',
  },
  crop_trouble: {
    id: 'crop_trouble',
    incubationHours: 12,
    recoveryHours: 48,
    contagious: false,
    remedy: 'fresh_feed_and_massage',
    factCardId: 'fact_crop',
  },
  pasting_up: {
    id: 'pasting_up',
    incubationHours: 6,
    recoveryHours: 12,
    contagious: false,
    remedy: 'warm_cloth_clean',
    factCardId: 'fact_pasting_up',
  },
  egg_bound: {
    id: 'egg_bound',
    incubationHours: 4,
    recoveryHours: 24,
    contagious: false,
    remedy: 'warm_bath_and_calcium',
    factCardId: 'fact_egg_bound',
  },
  wet_and_cold: {
    id: 'wet_and_cold',
    incubationHours: 2,
    recoveryHours: 12,
    contagious: false,
    remedy: 'dry_and_warm',
    factCardId: 'fact_wet_silkie',
  },
  heat_stress: {
    id: 'heat_stress',
    incubationHours: 1,
    recoveryHours: 8,
    contagious: false,
    remedy: 'shade_and_water',
    factCardId: 'fact_heat_stress',
  },
  scared: {
    id: 'scared',
    incubationHours: 0,
    recoveryHours: 24,
    contagious: false,
    remedy: 'calm_time_in_coop',
    factCardId: 'fact_fear',
  },
  dehydrated: {
    id: 'dehydrated',
    incubationHours: 0,
    recoveryHours: 24,
    contagious: false,
    remedy: 'water_and_vet',
    factCardId: 'fact_dehydration',
  },
  starving: {
    id: 'starving',
    incubationHours: 0,
    recoveryHours: 48,
    contagious: false,
    remedy: 'feed_and_vet',
    factCardId: 'fact_starvation',
  },
};

export function hasCondition(chicken: ChickenEntity, id: ConditionId): boolean {
  return chicken.conditions.some((c) => c.id === id);
}

export function addCondition(
  chicken: ChickenEntity,
  id: ConditionId,
  simHour: number,
  severity: ConditionSeverity = 'mild',
): ActiveCondition | null {
  if (hasCondition(chicken, id)) return null;
  const condition: ActiveCondition = {
    id,
    severity,
    startedAtHour: simHour,
    acknowledged: false,
  };
  chicken.conditions.push(condition);
  return condition;
}

export function removeCondition(chicken: ChickenEntity, id: ConditionId): void {
  chicken.conditions = chicken.conditions.filter((c) => c.id !== id);
}

/** Disease pressure the whole flock is under, from how the run is managed. */
export interface BiosecurityContext {
  hasFootbath: boolean;
  hasCoveredFeeder: boolean;
  spilledFeed: boolean;
  /** Sick birds not isolated raise everyone's risk. */
  contagiousUnquarantined: number;
  /** 0-1 rat pressure from spilled feed (PLAN-04 S5). */
  rodentPressure: number;
}

/**
 * How much a yard at full rat pressure multiplies its disease odds. Design
 * (GIMME-DATA D1/D2, ECO-06): no source states it, but the rats arrive by the
 * same spilled feed the wild birds do, so they scale the existing pressure term
 * rather than opening a second one.
 */
const RODENT_PRESSURE_SCALE = 0.25;

export function flockDiseasePressure(
  biosecurity: BiosecurityContext,
  config: BiologyConfig,
): number {
  let pressure = 1;
  if (biosecurity.hasFootbath) pressure *= 1 - config.disease.footbathPressureReduction;
  if (biosecurity.spilledFeed) {
    // Spilled feed draws wild birds, which is how disease actually arrives.
    pressure *= 1 + config.disease.wildBirdPressurePerSpill;
    if (biosecurity.hasCoveredFeeder) {
      pressure *= 1 - config.disease.coveredFeederPressureReduction;
    }
  }
  // Rats and spilled feed are the same chain, and the covered feeder and the
  // footbath that answer one answer the other.
  if (biosecurity.rodentPressure > 0) {
    pressure *= 1 + biosecurity.rodentPressure * RODENT_PRESSURE_SCALE;
    if (biosecurity.spilledFeed && biosecurity.hasCoveredFeeder) {
      pressure *= 1 - config.disease.coveredFeederPressureReduction;
    }
  }
  pressure *= 1 + biosecurity.contagiousUnquarantined * 0.25;
  return pressure;
}

/**
 * Rolls for new infections. Each candidate needs its real-world precondition
 * met first; immunity then decides whether the exposure takes hold.
 */
export function rollInfections(
  chicken: ChickenEntity,
  env: EnvContext,
  biosecurity: BiosecurityContext,
  hours: number,
  config: BiologyConfig,
  rng: Rng,
): ConditionId[] {
  const d = config.disease;
  const infected: ConditionId[] = [];
  // A strong immune system is largely protective; a suppressed one is not.
  const susceptibility = clamp((d.immuneGate - chicken.physiology.immune) / d.immuneGate, 0, 1);
  if (susceptibility <= 0) return infected;

  const pressure = flockDiseasePressure(biosecurity, config);
  const baseRoll = d.baseRollPerHour * hours * susceptibility * pressure;

  const candidates: Array<{ id: ConditionId; when: boolean; weight: number }> = [
    {
      id: 'coccidiosis',
      // Wet, dirty litter is the classic chick killer.
      when: env.litterDirtiness > d.coccidiosisLitterThreshold && chicken.stage !== 'adult',
      weight: 2.5,
    },
    {
      id: 'respiratory',
      when: chicken.physiology.stress > d.respiratoryStressThreshold || env.wetAndExposed,
      weight: 1.5,
    },
    {
      id: 'bumblefoot',
      when: env.litterDirtiness > d.bumblefootDirtyThreshold && chicken.stage === 'adult',
      weight: 1,
    },
    {
      id: 'scaly_leg_mites',
      when: env.litterDirtiness > d.mitesLitterThreshold,
      weight: 1.2,
    },
    {
      id: 'pasting_up',
      when: chicken.stage === 'chick' && (env.brooderTsi ?? 0) > config.brooder.stressTsiF,
      weight: 3,
    },
    {
      id: 'egg_bound',
      when: env.isLaying && !env.hasGrit && chicken.physiology.bodyCondition < 50,
      weight: 1,
    },
  ];

  for (const candidate of candidates) {
    if (!candidate.when || hasCondition(chicken, candidate.id)) continue;
    if (rng.chance(baseRoll * candidate.weight)) infected.push(candidate.id);
  }
  return infected;
}

/**
 * The hydration meter at which a bird counts as dehydrated. Named so the one
 * number the sim uses is the one number the Inspect card reads: a pale comb is
 * the visible sign of this condition (PHY-06), so the card that reports it has
 * to be standing on the sim's own line rather than a second one of its own.
 */
export const DEHYDRATED_HYDRATION = 20;

/**
 * Environmental conditions are immediate, not rolled — being rained on while
 * non-waterproof is not bad luck, it is physics.
 */
export function applyEnvironmentalConditions(
  chicken: ChickenEntity,
  env: EnvContext,
  simHour: number,
  config: BiologyConfig,
  silkieFeathered: boolean,
): ConditionId[] {
  const added: ConditionId[] = [];

  if (env.wetAndExposed && silkieFeathered && !hasCondition(chicken, 'wet_and_cold')) {
    addCondition(chicken, 'wet_and_cold', simHour, 'moderate');
    added.push('wet_and_cold');
  }
  if (
    env.tempF > config.temperature.severeHeatF &&
    !env.sheltered &&
    !hasCondition(chicken, 'heat_stress')
  ) {
    addCondition(chicken, 'heat_stress', simHour, 'moderate');
    added.push('heat_stress');
  }
  if (
    chicken.physiology.hydration <= DEHYDRATED_HYDRATION &&
    !hasCondition(chicken, 'dehydrated')
  ) {
    addCondition(chicken, 'dehydrated', simHour, 'severe');
    added.push('dehydrated');
  }
  if (chicken.physiology.energyReserve <= 20 && !hasCondition(chicken, 'starving')) {
    addCondition(chicken, 'starving', simHour, 'severe');
    added.push('starving');
  }
  return added;
}

/**
 * Recovery. Treated conditions clear on schedule; untreated ones worsen but
 * plateau at "severe" — pressure without grief (EDUCATION-DESIGN.md §5).
 */
export function stepConditions(
  chicken: ChickenEntity,
  env: EnvContext,
  simHour: number,
  config: BiologyConfig,
): ConditionId[] {
  const cleared: ConditionId[] = [];

  for (const condition of [...chicken.conditions]) {
    const definition = DISEASES[condition.id];
    const treated =
      condition.treatedUntilHour !== undefined && simHour >= condition.treatedUntilHour;

    // Some conditions clear the moment the cause is gone.
    const causeGone =
      (condition.id === 'wet_and_cold' && !env.wetAndExposed) ||
      (condition.id === 'heat_stress' && (env.sheltered || env.tempF <= config.temperature.comfortHighF)) ||
      (condition.id === 'dehydrated' && chicken.physiology.hydration > 45) ||
      (condition.id === 'starving' && chicken.physiology.energyReserve > 45);
    const elapsed = simHour - condition.startedAtHour;

    if (treated || (causeGone && elapsed >= definition.recoveryHours * 0.25)) {
      removeCondition(chicken, condition.id);
      cleared.push(condition.id);
      continue;
    }

    // Untreated: escalate on the definition's own timescale, then plateau.
    if (elapsed > definition.recoveryHours && condition.severity !== 'severe') {
      condition.severity = condition.severity === 'mild' ? 'moderate' : 'severe';
      condition.startedAtHour = simHour;
    }
  }
  return cleared;
}

export function treatCondition(
  chicken: ChickenEntity,
  id: ConditionId,
  simHour: number,
  /** Vet visits shortcut the recovery timer for Eggits. */
  vetVisit = false,
): boolean {
  const condition = chicken.conditions.find((c) => c.id === id);
  if (!condition) return false;
  const hours = DISEASES[id].recoveryHours * (vetVisit ? 0.25 : 1);
  condition.treatedUntilHour = simHour + hours;
  return true;
}
